import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useBusinessAuth } from "./BusinessAuthContext";
import * as msgSvc from "../../services/messagingService";

const BusinessMessagingContext = createContext(null);
const THREAD_POLL_INTERVAL_MS = 3000;
const SUMMARY_POLL_INTERVAL_MS = 15000;
const READ_RETRY_COOLDOWN_MS = 15000;

function toSellerActor(businessId) {
  const parsed = Number(businessId);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;

  return {
    type: "SELLER",
    businessId: parsed,
  };
}

function getSellerUnreadCount(conversation) {
  return Number(conversation?.seller_unread_count || 0);
}

function compareConversationActivity(left, right) {
  const leftTime = new Date(left?.last_message_at || left?.created_at || 0).getTime();
  const rightTime = new Date(right?.last_message_at || right?.created_at || 0).getTime();
  return rightTime - leftTime;
}

function patchConversationWithLatestMessage(conversation, message) {
  if (!conversation || !message) return conversation;

  return {
    ...conversation,
    last_message_text: message.message_text || "[Attachment]",
    last_message_at: message.created_at || conversation.last_message_at,
    seller_unread_count: 0,
  };
} 

function toConversationKey(value) {
  if (value == null) return '';
  return String(value);
}

function sumUnreadConversations(conversationList) {
  return conversationList.reduce((total, conversation) => total + getSellerUnreadCount(conversation), 0);
}

export function BusinessMessagingProvider({ children }) {
  const {
    loading: authLoading,
    isAuthenticated,
    activeBusinessId,
    currentBusiness,
  } = useBusinessAuth();

  const actor = useMemo(() => {
    if (!isAuthenticated) return null;
    return toSellerActor(activeBusinessId);
  }, [isAuthenticated, activeBusinessId]);

  const [conversations, setConversations] = useState([]);
  const [messagesById, setMessagesById] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessagesById, setLoadingMessagesById] = useState({});
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [markingReadConversationId, setMarkingReadConversationId] = useState(null);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [error, setError] = useState("");
  const actorKeyRef = useRef(null);
  const threadPollRef = useRef(null);
  const threadSyncInFlightRef = useRef(false);
  const conversationsRef = useRef([]);
  const activeConversationIdRef = useRef(null);
  const markReadRequestsRef = useRef(new Map());
  const markReadRetryAfterRef = useRef(new Map());

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const resetState = useCallback(() => {
    setConversations([]);
    setMessagesById({});
    setActiveConversationId(null);
    setUnreadTotal(0);
    setLoadingConversations(false);
    setLoadingMessagesById({});
    setSendingMessage(false);
    setDeletingMessageId(null);
    setMarkingReadConversationId(null);
    setIsInboxOpen(false);
    setError("");
    threadSyncInFlightRef.current = false;
    markReadRequestsRef.current.clear();
    markReadRetryAfterRef.current.clear();
  }, []);

  const openInbox = useCallback(() => setIsInboxOpen(true), []);
  const closeInbox = useCallback(() => setIsInboxOpen(false), []);
  const clearError = useCallback(() => setError(""), []);

  const refreshUnreadTotal = useCallback(async () => {
    if (!actor) {
      setUnreadTotal(0);
      return 0;
    }

    try {
      const count = await msgSvc.getUnreadCount(actor);
      const next = Number(count || 0);
      setUnreadTotal(next);
      return next;
    } catch {
      const fallbackUnread = sumUnreadConversations(conversationsRef.current || []);
      setUnreadTotal(fallbackUnread);
      return fallbackUnread;
    }
  }, [actor]);

  const refreshConversations = useCallback(async (options = {}) => {
    if (!actor) {
      resetState();
      return [];
    }

    const { silent = false } = options;
    if (!silent) {
      setLoadingConversations(true);
    }

    try {
      const [listResult, unreadCountResult] = await Promise.allSettled([
        msgSvc.listConversations(actor),
        msgSvc.getUnreadCount(actor),
      ]);

      if (listResult.status !== 'fulfilled') {
        throw listResult.reason;
      }

      const nextList = Array.isArray(listResult.value) ? listResult.value : [];
      conversationsRef.current = nextList;
      setConversations(nextList);
      setUnreadTotal(
        unreadCountResult.status === 'fulfilled'
          ? Number(unreadCountResult.value || 0)
          : sumUnreadConversations(nextList)
      );
      setActiveConversationId((currentId) => (
        currentId && nextList.some((conversation) => toConversationKey(conversation.id) === toConversationKey(currentId))
          ? currentId
          : null
      ));
      if (!silent) {
        setError("");
      }

      return nextList;
    } catch {
      if (!silent) {
        setError(actor ? "Unable to load this business inbox right now. Please try again." : "Select an active business to load messages.");
      }
      return [];
    } finally {
      if (!silent) {
        setLoadingConversations(false);
      }
    }
  }, [actor, resetState]);

  const loadMessages = useCallback(async (conversationId, options = {}) => {
    const targetConversationId = conversationId || activeConversationIdRef.current;
    if (!actor || !targetConversationId) return [];

    const { silent = false } = options;

    if (!silent) {
      setLoadingMessagesById((prev) => ({ ...prev, [targetConversationId]: true }));
    }
    try {
      const nextMessages = await msgSvc.getMessages(targetConversationId, {}, actor);
      const normalizedMessages = Array.isArray(nextMessages) ? nextMessages : [];
      setMessagesById((prev) => ({
        ...prev,
        [targetConversationId]: normalizedMessages,
      }));
      setError("");
      return normalizedMessages;
    } catch {
      setError("Unable to load this shopper conversation right now. Please try again.");
      return [];
    } finally {
      if (!silent) {
        setLoadingMessagesById((prev) => ({ ...prev, [targetConversationId]: false }));
      }
    }
  }, [actor]);

  const markConversationRead = useCallback(async (conversationId) => {
    const targetConversationId = conversationId || activeConversationIdRef.current;
    if (!actor || !targetConversationId) return false;

    const conversationKey = toConversationKey(targetConversationId);
    const existingRequest = markReadRequestsRef.current.get(conversationKey);
    if (existingRequest) return existingRequest;

    const conversation = conversationsRef.current.find(
      (item) => toConversationKey(item.id) === conversationKey
    );
    if (getSellerUnreadCount(conversation) <= 0) return true;

    const retryAfter = Number(markReadRetryAfterRef.current.get(conversationKey) || 0);
    if (retryAfter > Date.now()) return false;

    const unreadCount = getSellerUnreadCount(conversation);
    const optimisticConversations = conversationsRef.current.map((item) => (
      toConversationKey(item.id) === conversationKey
        ? { ...item, seller_unread_count: 0 }
        : item
    ));
    conversationsRef.current = optimisticConversations;
    setConversations(optimisticConversations);
    setUnreadTotal((current) => Math.max(0, Number(current || 0) - unreadCount));
    setMarkingReadConversationId(targetConversationId);

    const request = (async () => {
      try {
        await msgSvc.markConversationRead(targetConversationId, actor);
        markReadRetryAfterRef.current.delete(conversationKey);
        await refreshUnreadTotal();
        return true;
      } catch (readError) {
        markReadRetryAfterRef.current.set(
          conversationKey,
          Date.now() + READ_RETRY_COOLDOWN_MS
        );
        console.error(
          "[business messaging] unable to synchronize conversation read status:",
          msgSvc.getMessagingErrorMessage(readError)
        );
        return false;
      } finally {
        markReadRequestsRef.current.delete(conversationKey);
        setMarkingReadConversationId((currentId) => (
          toConversationKey(currentId) === conversationKey ? null : currentId
        ));
      }
    })();

    markReadRequestsRef.current.set(conversationKey, request);
    return request;
  }, [actor, refreshUnreadTotal]);

  const selectConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setActiveConversationId(null);
      return [];
    }

    clearError();
    setActiveConversationId(conversationId);

    const [messages] = await Promise.all([
      loadMessages(conversationId),
      markConversationRead(conversationId),
    ]);

    return messages;
  }, [clearError, loadMessages, markConversationRead]);

  const sendMessage = useCallback(async (conversationId, text, file = null) => {
    if (!actor || !conversationId) return null;

    setSendingMessage(true);
    try {
      const newMessage = await msgSvc.sendMessage(conversationId, text, file, actor);
      if (!newMessage) return null;

      setMessagesById((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), newMessage],
      }));

      setConversations((prev) => {
        const next = prev.map((conversation) => (
          conversation.id === conversationId
            ? patchConversationWithLatestMessage(conversation, newMessage)
            : conversation
        )).sort(compareConversationActivity);
        return next;
      });

      await refreshConversations({ silent: true });

      if (activeConversationId === conversationId) {
        await Promise.all([
          loadMessages(conversationId),
          markConversationRead(conversationId),
        ]);
      }

      setError("");
      return newMessage;
    } catch (err) {
      setError(msgSvc.getMessagingErrorMessage(
        err,
        "Unable to send your business reply right now. Please try again."
      ));
      return null;
    } finally {
      setSendingMessage(false);
    }
  }, [activeConversationId, actor, loadMessages, markConversationRead, refreshConversations]);

  const deleteMessage = useCallback(async (conversationId, messageId) => {
    if (!actor || !conversationId || !messageId) return false;

    setDeletingMessageId(messageId);
    try {
      await msgSvc.deleteMessage(conversationId, messageId, actor);
      setMessagesById((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((message) => message.id !== messageId),
      }));

      await refreshConversations({ silent: true });

      if (activeConversationId === conversationId) {
        await Promise.all([
          loadMessages(conversationId),
          markConversationRead(conversationId),
        ]);
      }

      setError("");
      return true;
    } catch {
      setError("Unable to delete that business message right now.");
      return false;
    } finally {
      setDeletingMessageId((currentId) => (
        currentId === messageId ? null : currentId
      ));
    }
  }, [activeConversationId, actor, loadMessages, markConversationRead, refreshConversations]);

  const searchConversations = useCallback(async (search) => {
    if (!actor) return [];

    const normalizedSearch = typeof search === 'string' ? search.trim() : '';
    if (!normalizedSearch) return conversationsRef.current || [];

    try {
      const results = await msgSvc.listConversations(actor, { q: normalizedSearch });
      setError("");
      return Array.isArray(results) ? results : [];
    } catch {
      setError("Unable to search business conversations right now.");
      return [];
    }
  }, [actor]);

  useEffect(() => {
    const nextActorKey = actor ? `${actor.type}:${actor.businessId}` : null;
    if (actorKeyRef.current === nextActorKey) return;

    actorKeyRef.current = nextActorKey;
    resetState();
  }, [actor, resetState]);

  useEffect(() => {
    if (authLoading) return;

    if (!actor) {
      resetState();
      return;
    }

    refreshConversations();
  }, [actor, authLoading, refreshConversations, resetState]);

  useEffect(() => {
    if (threadPollRef.current) {
      window.clearInterval(threadPollRef.current);
      threadPollRef.current = null;
    }

    if (!actor || !isInboxOpen || !activeConversationId) return undefined;

    const syncActiveConversation = async () => {
      if (threadSyncInFlightRef.current) return;

      threadSyncInFlightRef.current = true;
      try {
        await Promise.all([
          loadMessages(activeConversationId, { silent: true }),
          refreshConversations({ silent: true }),
        ]);
        await markConversationRead(activeConversationId);
      } finally {
        threadSyncInFlightRef.current = false;
      }
    };

    syncActiveConversation();

    threadPollRef.current = window.setInterval(() => {
      syncActiveConversation();
    }, THREAD_POLL_INTERVAL_MS);

    return () => {
      if (threadPollRef.current) {
        window.clearInterval(threadPollRef.current);
        threadPollRef.current = null;
      }
    };
  }, [
    actor,
    activeConversationId,
    isInboxOpen,
    loadMessages,
    markConversationRead,
    refreshConversations,
  ]);

  useEffect(() => {
    if (!actor) return undefined;
    if (isInboxOpen && activeConversationId) return undefined;

    const intervalId = window.setInterval(() => {
      refreshConversations({ silent: true });
    }, SUMMARY_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [actor, activeConversationId, isInboxOpen, refreshConversations]);

  const messages = useMemo(() => {
    if (!activeConversationId) return [];
    return messagesById[activeConversationId] || [];
  }, [activeConversationId, messagesById]);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => toConversationKey(conversation.id) === toConversationKey(activeConversationId)) || null,
    [conversations, activeConversationId]
  );

  const value = useMemo(() => ({
    actor,
    conversations,
    activeConversation,
    activeConversationId,
    messages,
    messagesById,
    unreadTotal,
    activeBusinessId,
    currentBusiness,
    error,
    selectConversation,
    sendMessage,
    deleteMessage,
    searchConversations,
    refreshConversations,
    loadMessages,
    markConversationRead,
    loading: authLoading,
    loadingConversations,
    loadingMessages: Boolean(activeConversationId && loadingMessagesById[activeConversationId]),
    loadingMessagesById,
    openInbox,
    closeInbox,
    clearError,
    sendingMessage,
    deletingMessageId,
    markingReadConversationId,
    getConversationUnreadCount: getSellerUnreadCount,
  }), [
    actor,
    conversations,
    activeConversation,
    activeConversationId,
    messages,
    messagesById,
    unreadTotal,
    activeBusinessId,
    currentBusiness,
    error,
    selectConversation,
    sendMessage,
    deleteMessage,
    searchConversations,
    refreshConversations,
    loadMessages,
    markConversationRead,
    authLoading,
    loadingConversations,
    loadingMessagesById,
    openInbox,
    closeInbox,
    clearError,
    sendingMessage,
    deletingMessageId,
    markingReadConversationId,
  ]);

  return (
    <BusinessMessagingContext.Provider value={value}>
      {children}
    </BusinessMessagingContext.Provider>
  );
}

export function useBusinessMessaging() {
  const context = useContext(BusinessMessagingContext);
  if (!context) {
    throw new Error("useBusinessMessaging must be used inside BusinessMessagingProvider");
  }

  return context;
}

export default BusinessMessagingContext;
